export default function About() {
  const stats = [
    { value: '10+', label: 'Years of Experience' },
    { value: '200+', label: 'Projects Delivered' },
    { value: '50+', label: 'Enterprise Clients' },
    { value: '98%', label: 'Client Satisfaction' },
  ];

  const values = [
    {
      icon: 'ri-shield-check-line',
      title: 'Integrity',
      description: 'We uphold the highest standards of professionalism and ethical conduct in every engagement.',
    },
    {
      icon: 'ri-lightbulb-flash-line',
      title: 'Innovation',
      description: 'Continuously adopting emerging technologies to deliver forward-thinking solutions.',
    },
    {
      icon: 'ri-team-line',
      title: 'Partnership',
      description: 'Building long-term relationships by working closely with clients as trusted advisors.',
    },
    {
      icon: 'ri-bar-chart-grouped-line',
      title: 'Excellence',
      description: 'Committed to delivering measurable results and sustainable business growth.',
    },
  ];

  return (
    <section id="about" className="py-14 md:py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-5 md:px-6">

        {/* Header */}
        <div className="text-center mb-10 md:mb-16">
          <div className="inline-block px-4 py-2 rounded-full border border-blue-500 bg-transparent mb-4 md:mb-6">
            <span className="text-blue-600 text-sm font-medium whitespace-nowrap">Who We Are</span>
          </div>
          <h2 className="text-xl md:text-3xl font-bold text-gray-900 mb-3 md:mb-4 leading-tight">
            Professional Consultancy & IT Solutions in the Philippines
          </h2>
          <p className="text-sm text-gray-500 max-w-2xl mx-auto leading-relaxed">
            Delivering innovative technology services and comprehensive business advisory since establishment
          </p>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6 mb-10 md:mb-16">
          {stats.map((stat, i) => (
            <div key={i} className="bg-white rounded-xl p-4 md:p-6 text-center shadow-sm border border-gray-100">
              <div className="text-2xl md:text-4xl font-bold text-gray-900 mb-1">{stat.value}</div>
              <div className="text-xs md:text-sm text-gray-500">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Main Content: Left text + Right image */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center mb-10 md:mb-16">
          <div>
            <h3 className="text-lg md:text-2xl font-bold text-gray-900 mb-4 md:mb-5 leading-snug">
              Your Trusted Technology &amp; Business Partner in the Philippines
            </h3>
            <p className="text-sm text-gray-600 leading-relaxed mb-3 md:mb-4">
              Hengtong Consultant & Solution is a professional consultancy and information technology services firm based in the Philippines. We specialize in delivering innovative IT solutions and comprehensive advisory services that support businesses in optimizing operations and achieving sustainable growth.
            </p>
            <p className="text-sm text-gray-600 leading-relaxed mb-3 md:mb-4">
              Our IT services focus on providing reliable, scalable, and efficient solutions tailored to the evolving needs of modern enterprises — from system support and technology advisory to operational integration.
            </p>
            <p className="text-sm text-gray-600 leading-relaxed mb-5 md:mb-6">
              We also offer general consultancy services including strategic planning, operational management support, and business advisory, working closely with corporations, firms, and entrepreneurs.
            </p>
            <a
              href="#contact"
              className="inline-flex items-center gap-2 bg-gray-900 text-white px-5 py-2.5 md:px-6 md:py-3 rounded-full text-sm font-medium hover:bg-gray-700 transition-all whitespace-nowrap cursor-pointer"
            >
              Get in Touch
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>

          <div className="relative mt-4 md:mt-0">
            <img
              src="https://readdy.ai/api/search-image?query=professional%20business%20team%20meeting%20in%20modern%20office%2C%20diverse%20group%20of%20consultants%20discussing%20strategy%20around%20a%20conference%20table%20with%20laptops%20and%20documents%2C%20bright%20contemporary%20workspace%20with%20large%20windows%2C%20collaborative%20corporate%20environment%2C%20clean%20minimalist%20interior%20design%2C%20natural%20lighting%2C%20high%20quality%20business%20photography&width=700&height=500&seq=about-team-001&orientation=landscape"
              alt="Our Team"
              className="w-full h-52 md:h-72 object-cover object-top rounded-2xl shadow-md"
            />
            <div className="absolute -bottom-4 left-4 md:-bottom-5 md:-left-5 bg-white rounded-xl shadow-lg px-4 py-3 md:px-6 md:py-4 flex items-center gap-2 md:gap-3">
              <div className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center bg-green-100 rounded-full">
                <i className="ri-map-pin-2-line text-green-600 text-base md:text-lg"></i>
              </div>
              <div>
                <div className="text-xs text-gray-400">Headquartered in</div>
                <div className="text-xs md:text-sm font-semibold text-gray-800">Philippines</div>
              </div>
            </div>
          </div>
        </div>

        {/* Core Values */}
        <div>
          <div className="text-center mb-7 md:mb-10">
            <h3 className="text-lg md:text-xl font-bold text-gray-900">Our Core Values</h3>
            <p className="text-sm text-gray-500 mt-2">The principles that guide everything we do</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6">
            {values.map((v, i) => (
              <div key={i} className="bg-white rounded-xl p-4 md:p-6 border border-gray-100 shadow-sm hover:shadow-md transition-all cursor-pointer group">
                <div className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center bg-gray-100 rounded-lg mb-3 md:mb-4 group-hover:bg-gray-900 transition-colors">
                  <i className={`${v.icon} text-lg md:text-xl text-gray-700 group-hover:text-white transition-colors`}></i>
                </div>
                <h4 className="text-sm md:text-base font-semibold text-gray-900 mb-1 md:mb-2">{v.title}</h4>
                <p className="text-xs text-gray-500 leading-relaxed">{v.description}</p>
              </div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
}